import {
  Box,
  Button,
  Snackbar,
  Alert,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  RadioGroup,
  Radio,
  Grid,
  InputAdornment,
  FormHelperText,
  Paper,
} from '@mui/material';
import * as Yup from 'yup';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import {
  CuffaProductionTypeEnum,
  CuffaMeasurementUnitEnum,
  CuffaProductTypeEnum,
  CuffaManagementTypeEnum,
  CuffaAgroecologicalCertificationEnum
} from 'common/enums';
import DeleteIcon from '@mui/icons-material/Delete';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { formatCurrency, getAllowedUnits } from 'utils/forms/cuffa_functions';
import {
  computeProductionTypes,
  toggleProductSelection,
  buildOtherProduct,
  updateSelectedProductsQuantity,
  updateSelectedProductsUnit,
  removeProductByKey,
  updateAvailabilityMonths,
} from './helpers';
import validationSchema, { initialValues, months } from './validation';
import { getEnumValues } from './utils';
import { createHandleSubmit } from './handlers';
import OtherProductForm from './OtherProductForm';
import SelectedProductsList from './SelectedProductsList';
import useCuffaProductsData from './useCuffaProductsData';
import { mapApiCertifications, computeFocusParams } from './more_helpers';
import FormErrorDisplay, { FormArrayErrorDisplay, FormObjectErrorDisplay } from './FormHelpers';

// Minimal shape for availability months used across the form
type AvailabilityMonths = { available_year_round?: boolean } & Record<string, boolean | undefined>;

export interface ICuffaProduct {
  id?: string;
  uuid?: string;
  name: string;
  production_type: CuffaProductionTypeEnum;
  product_type: CuffaProductTypeEnum;
  is_processed: boolean;
  is_active: boolean;
  quantity?: number;
  unit?: CuffaMeasurementUnitEnum;
}

interface ProductFormData {
  productId?: string;
  name?: string;
  product_type: CuffaProductTypeEnum;
  production_type: CuffaProductionTypeEnum;
  management_type?: CuffaManagementTypeEnum;
  is_processed?: boolean;
  produced_quantity?: number;
  measurement_unit?: CuffaMeasurementUnitEnum;
  area_hectares?: number;
  volume_produced?: number;
  volume_commercialized?: number;
  commercialized_value?: number;
  volume_consumed_donated?: number;
  volume_lost?: number;
  availability_months?: AvailabilityMonths;
  has_certification?: boolean;
  certification_type?: CuffaAgroecologicalCertificationEnum;
  other_certification_name?: string;
}

interface FormValues {
  productionTypes: CuffaProductionTypeEnum[];
  selectedProducts: ICuffaProduct[];
  managementType: CuffaManagementTypeEnum | '';

  // Campos detalhados
  area_hectares: number | '';
  volume_produced: number | '';
  volume_commercialized: number | '';
  commercialized_value: string;
  volume_consumed_donated: number | '';
  volume_lost: number | '';

  // Disponibilidade
  availability_months: AvailabilityMonths;

  // Certificação
  has_certification: boolean | null;
  certification_type: CuffaAgroecologicalCertificationEnum | '';
  other_certification_name: string;
}

// =============================================
// Constantes e Valores Iniciais
// =============================================


// initialValues, months and validationSchema moved to ./validation

// months value imported from ./validation

// validation moved to ./validation.ts

// Form error display helpers are provided by ./FormHelpers to avoid duplication

// =============================================
// Componente Principal
// =============================================
export const CadratroProdutosIncluir: React.FC = () => {
  // =============================================
  // Estados
  // =============================================
  const [saving, setSaving] = useState(false);
  const [loadingState, setLoadingState] = useState<'salvar' | 'avancar' | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  // products, productsById and dados are provided by hook
  const [snackbarError, setSnackbarError] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const [otherProductName, setOtherProductName] = useState('');
  const [otherProductType, setOtherProductType] = useState<CuffaProductionTypeEnum | ''>('');
  const [isProcessed, setIsProcessed] = useState(false);
  const [showOtherProductForm, setShowOtherProductForm] = useState(false);
  const advanceNext = useRef(false);

  // =============================================
  // Formik
  // =============================================

  const handleSubmit = createHandleSubmit({
    uuid,
    navigate,
    setSnackbarMessage,
    setSnackbarError,
    setOpenSnackbar,
    setSaving,
    setLoadingState,
  });

  const formik = useFormik<FormValues>({
    initialValues: initialValues as any,
    validationSchema: validationSchema as any,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: handleSubmit,
  });

  // Use custom hook to encapsulate data fetching and form population
  const { products, productsById, dados } = useCuffaProductsData({
    uuid,
    productionTypes: formik.values.productionTypes,
    formik: (formik as unknown) as any,
    reportError: (message: string) => {
      setSnackbarMessage(message);
      setSnackbarError(true);
      setOpenSnackbar(true);
    },
  });







  // =============================================
  // Funções de Manipulação de Dados
  // =============================================



  function handleCloseSnackbar() {
    setOpenSnackbar(false);
  }

  function handleProductionTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked } = event.target;
    const { productionTypes: newArray, selectedProducts: newSelectedProducts } = computeProductionTypes(
      formik.values.productionTypes as any,
      formik.values.selectedProducts as any,
      value,
      checked,
    );
    formik.setFieldValue('productionTypes', newArray);
    formik.setFieldValue('selectedProducts', newSelectedProducts);
  }

  function handleProductCheckboxChange(
    event: React.ChangeEvent<HTMLInputElement>,
    product: ICuffaProduct,
  ) {
    const { checked } = event.target;
    const newSelectedProducts = toggleProductSelection(formik.values.selectedProducts as any, product, checked);
    formik.setFieldValue('selectedProducts', newSelectedProducts);
  }

  function handleAddOtherProduct() {
    if (otherProductName.trim() !== '' && otherProductType !== '') {
      const newOtherProduct = buildOtherProduct(otherProductName, otherProductType, isProcessed);
      const newSelectedProducts = [...formik.values.selectedProducts, newOtherProduct];
      formik.setFieldValue('selectedProducts', newSelectedProducts);
      setOtherProductName('');
      setOtherProductType('');
      setIsProcessed(false);
      setShowOtherProductForm(false);
    }
  }

  function handleQuantityChange(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    productKey: string
  ) {
    const newQuantity = Number(event.target.value);
    const newSelectedProducts = updateSelectedProductsQuantity(formik.values.selectedProducts as any, productKey, newQuantity);
    formik.setFieldValue('selectedProducts', newSelectedProducts);
  }

  function handleUnitChange(event: any, productKey: string) {
    const newUnit = event.target.value as CuffaMeasurementUnitEnum;
    const newSelectedProducts = updateSelectedProductsUnit(formik.values.selectedProducts as any, productKey, newUnit);
    formik.setFieldValue('selectedProducts', newSelectedProducts);
  }

  function handleRemoveProduct(productKey: string) {
    const newSelectedProducts = removeProductByKey(formik.values.selectedProducts as any, productKey);
    formik.setFieldValue('selectedProducts', newSelectedProducts);
  }

  // small util moved to ./utils but keep local reference for backwards compatibility
  // (some inline code still calls getEnumValues)
  // getEnumValues imported above

  function handleMonthChange(monthKey: string, checked: boolean) {
    const newAvailability = updateAvailabilityMonths(formik.values.availability_months as any, monthKey, checked, months as any);
    formik.setFieldValue('availability_months', newAvailability);
  }

  function handleAction(actionType: 'salvar' | 'avancar') {
    setLoadingState(actionType);
    advanceNext.current = actionType === 'avancar';
    formik.handleSubmit();
  }

  // Verificar se há erros no formulário
  const shouldShowComplementaryQuestions = formik.values.managementType !== '';

  // =============================================
  // Componentes de Renderização
  // =============================================

  // Componentes auxiliares de renderização (definidos no topo do arquivo)

  // Reusable block for rendering production detail sections (reduces duplication)
  const ProductionDetails: React.FC<{
    title: string;
    areaLabel: string;
    producedLabel: string;
    commercialLabel: string;
    certificationTitle: string;
  }> = ({ title, areaLabel, producedLabel, commercialLabel, certificationTitle }) => (
    <Paper elevation={1} sx={{ mt: 4, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="area_hectares"
            type="number"
            label={areaLabel}
            onBlur={formik.handleBlur}
            value={formik.values.area_hectares}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.area_hectares && formik.touched.area_hectares)}
            helperText={formik.errors.area_hectares && formik.touched.area_hectares ? formik.errors.area_hectares : ''}
            InputProps={{ inputProps: { step: "0.01", min: "0" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="volume_produced"
            label={producedLabel}
            type="number"
            value={formik.values.volume_produced}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.volume_produced && formik.touched.volume_produced)}
            helperText={formik.errors.volume_produced && formik.touched.volume_produced ? formik.errors.volume_produced : ''}
            InputProps={{ inputProps: { step: "0.01", min: "0" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="volume_commercialized"
            label="Volume comercializado no último ano (kg ou toneladas)"
            type="number"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.volume_commercialized && formik.touched.volume_commercialized)}
            value={formik.values.volume_commercialized}
            helperText={formik.errors.volume_commercialized && formik.touched.volume_commercialized ? formik.errors.volume_commercialized : ''}
            InputProps={{ inputProps: { step: "0.01", min: "0" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="commercialized_value"
            label={commercialLabel}
            value={formik.values.commercialized_value}
            onChange={(e) => {
              const formatted = formatCurrency(e.target.value);
              formik.setFieldValue('commercialized_value', formatted);
            }}
            onBlur={formik.handleBlur}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="volume_consumed_donated"
            label="Volume consumido, doado ou trocado (kg ou toneladas)"
            type="number"
            onChange={formik.handleChange}
            value={formik.values.volume_consumed_donated}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.volume_consumed_donated && formik.touched.volume_consumed_donated)}
            helperText={formik.errors.volume_consumed_donated && formik.touched.volume_consumed_donated ? formik.errors.volume_consumed_donated : ''}
            InputProps={{ inputProps: { step: "0.01", min: "0" } }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="volume_lost"
            type="number"
            label="Volume perdido (kg ou toneladas)"
            value={formik.values.volume_lost}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.volume_lost && formik.touched.volume_lost)}
            helperText={formik.errors.volume_lost && formik.touched.volume_lost ? formik.errors.volume_lost : ''}
            InputProps={{ inputProps: { step: "0.01", min: "0" } }}
          />
        </Grid>
      </Grid>

      {/* Seção de Certificação Agroecológica */}
      <Box mt={3}>
        <Typography variant="subtitle1" gutterBottom>
          {certificationTitle}
        </Typography>

        <FormControl
          component="fieldset"
          error={Boolean(formik.errors.has_certification && formik.touched.has_certification)}
        >
          <RadioGroup
            name="has_certification"
            value={formik.values.has_certification === null ? '' : String(formik.values.has_certification)}
            onChange={(e) => {
              const value = e.target.value === 'true';
              formik.setFieldValue('has_certification', value);
              if (!value) {
                formik.setFieldValue('certification_type', '');
                formik.setFieldValue('other_certification_name', '');
              }
            }}
          >
            <FormControlLabel value="true" control={<Radio />} label="Sim" />
            <FormControlLabel value="false" control={<Radio />} label="Não" />
          </RadioGroup>

          <FormErrorDisplay
            error={formik.errors.has_certification as string}
            touched={formik.touched.has_certification}
          />
        </FormControl>

        {/* Tipos de Certificação */}
        {formik.values.has_certification && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Marque a certificação que se aplica: *
            </Typography>

            <FormControl
              component="fieldset"
              error={Boolean(formik.errors.certification_type && formik.touched.certification_type)}
              fullWidth
            >
              <RadioGroup
                name="certification_type"
                onChange={(e) => {
                  formik.setFieldValue('certification_type', e.target.value);
                  if (e.target.value !== CuffaAgroecologicalCertificationEnum.OUTRO) {
                    formik.setFieldValue('other_certification_name', '');
                  }
                }}
                value={formik.values.certification_type}

              >
                <FormControlLabel
                  value={CuffaAgroecologicalCertificationEnum.CERTIFICADORA_POR_AUDITORIA}
                  control={<Radio />}
                  label="Certificadora por Auditoria"
                />
                <FormControlLabel
                  value={CuffaAgroecologicalCertificationEnum.ORGANISMO_PARTICIPATIVO_OPAC}
                  control={<Radio />}
                  label="Organismo Participativo de Avaliação da Conformidade - OPAC"
                />
                <FormControlLabel
                  value={CuffaAgroecologicalCertificationEnum.ORGANIZACAO_CONTROLE_SOCIAL_OCS}
                  control={<Radio />}
                  label="Organização de Controle Social – OCS"
                />
                <FormControlLabel
                  value={CuffaAgroecologicalCertificationEnum.OUTRO}
                  label="Outro:"
                  control={<Radio />}

                />
              </RadioGroup>
              <FormErrorDisplay
                error={formik.errors.certification_type}
                touched={formik.touched.certification_type}
              />
            </FormControl>

            {formik.values.certification_type === CuffaAgroecologicalCertificationEnum.OUTRO && (
              <Box mt={2}>
                <TextField
                  fullWidth
                  name="other_certification_name"
                  label="Especificar outro tipo de certificação *"
                  value={formik.values.other_certification_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.errors.other_certification_name && formik.touched.other_certification_name)}
                  helperText={formik.errors.other_certification_name && formik.touched.other_certification_name ? formik.errors.other_certification_name : ''}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );

  // =============================================
  // Renderização Principal
  // =============================================
  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Seleção de Produtos Produzidos" showBackButton />

      <Box display="flex" justifyContent="center" mt={4}>
        <ContainerForm title="Cadastro de Produtos" width='1152px'>
          <form onSubmit={formik.handleSubmit}>

            {/* <GeneralErrorAlert /> */}

            {/* ETAPA 1: Tipo de Produção */}
            <Box mb={4}>
              <Typography variant="h6" gutterBottom>
                1. Selecione o Tipo de Produção *
              </Typography>
              <FormControl
                component="fieldset"
                error={Boolean(formik.errors.productionTypes && formik.touched.productionTypes)}
                fullWidth
              >
                <FormGroup>
                  {getEnumValues(CuffaProductionTypeEnum).map((value) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          name="productionTypes"
                          checked={formik.values.productionTypes.includes(value as CuffaProductionTypeEnum)}
                          onChange={handleProductionTypeChange}
                          value={value}
                        />
                      }
                      label={value}
                    />
                  ))}
                </FormGroup>
                <FormErrorDisplay
                  error={formik.errors.productionTypes as string}
                  touched={formik.touched.productionTypes}
                />
              </FormControl>
            </Box>

            {/* ETAPA 2: Seleção de Produtos */}
            {formik.values.productionTypes.length > 0 && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  2. Selecione os Produtos das Listas *
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Selecione cada produto e informe a quantidade para cada um
                </Typography>

                <FormControl
                  component="fieldset"
                  error={Boolean(formik.errors.selectedProducts && formik.touched.selectedProducts)}
                  fullWidth
                >
                  <FormGroup
                    sx={{
                      mt: 2,
                      columnCount: 3,
                      columnGap: 16,
                      maxHeight: 300,
                      overflowY: 'auto',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showOtherProductForm}
                          onChange={(e) => setShowOtherProductForm(e.target.checked)}
                          inputProps={{ 'data-testid': 'product-checkbox-other' } as any}
                        />
                      }
                      label="Outro"
                      sx={{ display: 'block', breakInside: 'avoid' }}
                    />

                    {products
                      .filter(product => formik.values.productionTypes.includes(product.production_type))
                      .map((product, idx) => (
                        <FormControlLabel
                          key={product.id ?? product.uuid ?? idx}
                          control={
                            <Checkbox
                              onChange={(e) => handleProductCheckboxChange(e, product)}
                              checked={formik.values.selectedProducts.some(
                                (p) => (p.id ?? p.uuid) === (product.id ?? product.uuid)
                              )}
                              inputProps={{ 'data-testid': `product-checkbox-${idx}` } as any}
                            />
                          }
                          label={product.name}
                          sx={{
                            display: 'block',
                            breakInside: 'avoid',
                          }}
                        />
                      ))}
                  </FormGroup>

                  <FormArrayErrorDisplay
                    error={formik.errors.selectedProducts as string}
                    submitCount={formik.submitCount}
                  />
                </FormControl>

                {/* Adicionar Outro Produto */}
                <OtherProductForm
                  showOtherProductForm={showOtherProductForm}
                  otherProductName={otherProductName}
                  setOtherProductName={setOtherProductName}
                  otherProductType={otherProductType}
                  setOtherProductType={setOtherProductType}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  productionTypes={formik.values.productionTypes}
                  handleAddOtherProduct={handleAddOtherProduct}
                />

                <SelectedProductsList
                  selectedProducts={formik.values.selectedProducts}
                  formik={formik}
                  handleQuantityChange={handleQuantityChange}
                  handleUnitChange={handleUnitChange}
                  handleRemoveProduct={handleRemoveProduct}
                  getAllowedUnits={getAllowedUnits}
                />
              </Box>
            )}

            {/* ETAPA 3: Tipo de Manejo */}
            {formik.values.selectedProducts.length > 0 && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  3. Selecione o Tipo de Manejo/Produção *
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Ordem: Agroecológica, Sistemas Agroflorestais (SAF), Orgânica, Convencional
                </Typography>

                <FormControl
                  component="fieldset"
                  error={Boolean(formik.errors.managementType && formik.touched.managementType)}
                  fullWidth
                >
                  <RadioGroup
                    row
                    name="managementType"
                    value={formik.values.managementType ?? ''}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value={CuffaManagementTypeEnum.AGROECOLOGICA}
                      control={<Radio />}
                      label="Agroecológica"
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label="Sistemas Agroflorestais (SAF)"
                      value={CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF}
                    />
                    <FormControlLabel
                      value={CuffaManagementTypeEnum.ORGANICA}
                      control={<Radio />}
                      label="Orgânica"
                    />
                    <FormControlLabel
                      value={CuffaManagementTypeEnum.CONVENCIONAL}
                      control={<Radio />}
                      label="Convencional"
                    />
                  </RadioGroup>
                  <FormErrorDisplay
                    error={formik.errors.managementType}
                    touched={formik.touched.managementType}
                  />
                </FormControl>
              </Box>
            )}

            {/* ETAPA 4: Perguntas Complementares */}
            {shouldShowComplementaryQuestions && (
              <Box mb={4}>
                <Typography variant="h6" gutterBottom>
                  4. Informações Complementares
                </Typography>

                {/* Meses de Disponibilidade */}
                <Box mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Em quais meses do ano os produtos estão disponíveis para comercialização? *
                  </Typography>

                  <FormControl
                    component="fieldset"
                    error={Boolean(formik.errors.availability_months && formik.touched.availability_months)}
                    fullWidth
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formik.values.availability_months?.available_year_round)}
                          onChange={(e) => handleMonthChange('available_year_round', e.target.checked)}
                        />
                      }
                      label="Disponível o ano todo"
                      sx={{ mb: 2, fontWeight: 'bold' }}
                    />

                    {/* Meses individuais */}
                    <Grid container spacing={2}>
                      {months.map((month) => (
                        <Grid item xs={4} sm={3} md={2} key={month.key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(formik.values.availability_months?.[month.key])}
                                onChange={(e) => handleMonthChange(month.key, e.target.checked)}
                                disabled={formik.values.availability_months?.available_year_round}
                              />
                            }
                            label={month.label}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <FormObjectErrorDisplay
                      error={typeof formik.errors.availability_months === 'string' ? formik.errors.availability_months : undefined}
                      submitCount={formik.submitCount}
                    />
                  </FormControl>
                </Box>

                {formik.values.managementType === CuffaManagementTypeEnum.AGROECOLOGICA && (
                  <ProductionDetails
                    title="Dados da Produção Agroecológica"
                    areaLabel="Total de área destinada (hectares) *"
                    producedLabel="Volume produzido (kg ou toneladas) *"
                    commercialLabel="Valor comercializado no último ano"
                    certificationTitle="A unidade familiar possui certificação de produção agroecológica? *"
                  />
                )}

                {formik.values.managementType === CuffaManagementTypeEnum.SISTEMAS_AGROFLORESTAIS_SAF && (
                  <ProductionDetails
                    title="Dados da Produção em Sistemas Agroflorestais (SAF)"
                    areaLabel="Área total do SAF (hectares) *"
                    producedLabel="Volume total produzido (kg ou toneladas) *"
                    commercialLabel="Valor comercializado no último ano"
                    certificationTitle="A unidade familiar possui certificação de produção agroecológica/orgânica/SAF? *"
                  />
                )}
              </Box>
            )}

            {/* Botões de Ação */}
            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.CATEGORIA_FUNDIARIA_INCLUIR.replace(':uuid', uuid ?? ''))}
                disabled={saving}
              >
                Retornar
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleAction('salvar')}
                disabled={saving}
              >
                {saving && loadingState === 'salvar' ? (
                  <CircularProgress size={24} color="inherit" />
                ) : 'Salvar'}
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAction('avancar')}
                disabled={saving}
              >
                {saving && loadingState === 'avancar' && (
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                )}
                Avançar
              </Button>
            </Box>
          </form>
        </ContainerForm>
      </Box>

      {/* Snackbar de Feedback */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbarError ? 'error' : 'success'}
          sx={{ width: '100%' }}
          onClose={handleCloseSnackbar}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};

// Re-export form helper components so tests and other modules can import from this
// directory's index file (import { FormErrorDisplay } from './index')
export { FormErrorDisplay, FormArrayErrorDisplay, FormObjectErrorDisplay };

export default CadratroProdutosIncluir;
